import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { joinWaitlist } from "@/lib/api";
import Reveal from "./Reveal";

export const Waitlist = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const res = await joinWaitlist({ name: name || null, email, message: message || null });
      setDone(true);
      toast.success(res.message || "You're on the list.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="py-24 md:py-40" data-testid="waitlist-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <p className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-[#2B2B26]/60 mb-6">
            Early access
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#2B2B26] mb-6">
            Join the calm.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-body text-lg leading-relaxed text-[#2B2B26]/70 font-light max-w-xl mx-auto mb-12">
            Be among the first to make My Life your daily companion. We'll send a gentle note
            the moment early access opens.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#EDE8DF] rounded-[2rem] p-10 border border-[#2B2B26]/5"
              data-testid="waitlist-success"
            >
              <p className="font-serif text-3xl text-[#2B2B26] mb-3">Thank you.</p>
              <p className="font-body text-[#2B2B26]/70">
                You're on the list — a warm welcome is on its way to your inbox.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-4 text-left" data-testid="waitlist-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  data-testid="waitlist-name-input"
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-full bg-[#EDE8DF] border border-[#2B2B26]/10 px-6 py-4 font-body text-[#2B2B26] placeholder-[#2B2B26]/40 outline-none focus:border-[#1877F2] transition-colors"
                />
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-full bg-[#EDE8DF] border border-[#2B2B26]/10 px-6 py-4 font-body text-[#2B2B26] placeholder-[#2B2B26]/40 outline-none focus:border-[#1877F2] transition-colors"
                />
              </div>
              <textarea
                data-testid="waitlist-message-input"
                placeholder="Anything you'd love My Life to help with? (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="w-full rounded-[1.5rem] bg-[#EDE8DF] border border-[#2B2B26]/10 px-6 py-4 font-body text-[#2B2B26] placeholder-[#2B2B26]/40 outline-none focus:border-[#1877F2] transition-colors resize-none"
              />
              <button
                data-testid="waitlist-submit-btn"
                type="submit"
                disabled={loading}
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2B2B26] text-[#F8F7F1] px-9 py-4 font-body text-sm font-medium transition-all duration-300 hover:bg-[#2B2B26]/90 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Joining…
                  </>
                ) : (
                  <>
                    Join the waitlist
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.25}>
          <p className="font-body text-sm text-[#2B2B26]/60 mt-10" data-testid="waitlist-contact-note">
            Have a question or need help?{" "}
            <a
              data-testid="waitlist-contact-email"
              href="mailto:info@mylifeorg.tech"
              className="text-[#1877F2] underline underline-offset-4 hover:text-[#2B2B26] transition-colors"
            >
              info@mylifeorg.tech
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Waitlist;
